import { PermissionAction, PermissionResource } from '@sib/api';
import type { Permission } from '@sib/auth';

export interface PermissionCheck {
  permissions: Permission[] | Permission;
  operator?: 'AND' | 'OR';
}

export const permissions = {
  admin: {
    edit: {
      resources: PermissionResource.Admin,
      actions: PermissionAction.UPDATE,
    },
  },
  sibServices: {
    view: {
      resources: PermissionResource.Billings,
      actions: PermissionAction.READ,
    },
    edit: {
      resources: PermissionResource.Billings,
      actions: PermissionAction.UPDATE,
    },
    create: {
      resources: PermissionResource.Billings,
      actions: PermissionAction.CREATE,
    },
    delete: {
      resources: PermissionResource.Billings,
      actions: PermissionAction.DELETE,
    },
  },
  audits: {
    view: {
      resources: PermissionResource.Audits,
      actions: PermissionAction.READ,
    },
    create: {
      resources: PermissionResource.Audits,
      actions: PermissionAction.CREATE,
    },
    edit: {
      resources: PermissionResource.Audits,
      actions: PermissionAction.UPDATE,
    },
  },
  auditTasks: {
    view: {
      resources: PermissionResource.AuditTasks,
      actions: PermissionAction.READ,
    },
    create: {
      resources: PermissionResource.AuditTasks,
      actions: PermissionAction.CREATE,
    },
    edit: {
      resources: PermissionResource.AuditTasks,
      actions: PermissionAction.UPDATE,
    },
  },
  actionItems: {
    view: {
      resources: PermissionResource.ActionItems,
      actions: PermissionAction.READ,
    },
    create: {
      resources: PermissionResource.ActionItems,
      actions: PermissionAction.CREATE,
    },
    edit: {
      resources: PermissionResource.ActionItems,
      actions: PermissionAction.UPDATE,
    },
    delete: {
      resources: PermissionResource.ActionItems,
      actions: PermissionAction.DELETE,
    },
  },
  tickets: {
    view: {
      resources: PermissionResource.Tickets,
      actions: PermissionAction.READ,
    },
    create: {
      resources: PermissionResource.Tickets,
      actions: PermissionAction.CREATE,
    },
    // Covers all in-form ticket mutations (assign / manage workflow / close).
    // The CRUD bitmask can't separate those sub-actions; the backend enforces
    // the finer split.
    edit: {
      resources: PermissionResource.Tickets,
      actions: PermissionAction.UPDATE,
    },
  },
  savings: {
    view: {
      resources: PermissionResource.Savings,
      actions: PermissionAction.READ,
    },
  },
  // Cross-org resource: gated on globalActionPermissions, not actionPermissions
  // — see useUserSetupStore. Inert until GET /user/current returns a mask.
  projects: {
    create: {
      resources: PermissionResource.Projects,
      actions: PermissionAction.CREATE,
    },
    edit: {
      resources: PermissionResource.Projects,
      actions: PermissionAction.UPDATE,
    },
  },
  // Cross-org resource: gated on globalActionPermissions via `scope="global"` /
  // useCanGlobal, like projects. Inert (allow-all) until GET /user/current
  // returns a mask.
  teams: {
    view: {
      resources: PermissionResource.Teams,
      actions: PermissionAction.READ,
    },
  },
  inactiveSibContracts: {
    assign: {
      resources: PermissionResource.InactiveSibContracts,
      actions: PermissionAction.CREATE,
    },
  },
  internalDocuments: {
    view: {
      resources: PermissionResource.InternalDocuments,
      actions: PermissionAction.READ,
    },
    create: {
      resources: PermissionResource.InternalDocuments,
      actions: PermissionAction.CREATE,
    },
    edit: {
      resources: PermissionResource.InternalDocuments,
      actions: PermissionAction.UPDATE,
    },
    delete: {
      resources: PermissionResource.InternalDocuments,
      actions: PermissionAction.DELETE,
    },
  },
  onlineAccess: {
    view: {
      resources: PermissionResource.Organizations,
      actions: PermissionAction.READ,
    },
    create: {
      resources: PermissionResource.Organizations,
      actions: PermissionAction.CREATE,
    },
    edit: {
      resources: PermissionResource.Organizations,
      actions: PermissionAction.UPDATE,
    },
    credentials: {
      resources: PermissionResource.OnlineAccessCredentials,
      actions: PermissionAction.READ,
    },
    credentialsCreate: {
      resources: PermissionResource.OnlineAccessCredentials,
      actions: PermissionAction.CREATE,
    },
    credentialsUpdate: {
      resources: PermissionResource.OnlineAccessCredentials,
      actions: PermissionAction.UPDATE,
    },
  },
} satisfies Record<string, Record<string, Permission>>;
